import React, { useState, useEffect } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { clipboardService } from '../services/api';
import toast from 'react-hot-toast';

const HorizontalVoteButtons = ({ itemId, initialVotes = 0, onVoteChange }) => {
  const [userVote, setUserVote] = useState(null); // 'upvote', 'downvote', or null
  const [votes, setVotes] = useState(initialVotes);
  const [loading, setLoading] = useState(false);
  const [endpointsAvailable, setEndpointsAvailable] = useState(true);

  // Fetch user's current vote and vote count when component mounts
  useEffect(() => {
    const fetchVoteData = async () => {
      try {
        // Fetch user's vote
        const userVoteResponse = await clipboardService.getUserVote(itemId);
        setUserVote(userVoteResponse.vote);
        
        // Fetch current vote count if available
        if (userVoteResponse.votes !== undefined) {
          setVotes(userVoteResponse.votes);
        } else {
          // Use initialVotes if API doesn't provide vote count
          setVotes(initialVotes);
        }
      } catch (error) {
        console.error('Error fetching vote data:', error);
        console.error('Error response:', error.response?.data);
        // Don't show error toast for this - it's expected if user is not logged in or endpoints don't exist yet
        if (error.response?.status === 404) {
          setEndpointsAvailable(false);
        }
        // Use initialVotes as fallback
        setVotes(initialVotes);
      }
    };

    fetchVoteData();
  }, [itemId, initialVotes]);

  const handleUpvote = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      const response = await clipboardService.upvoteItem(itemId);
      console.log('Upvote response:', response);
      
      // Update local state based on API response
      if (response.action === 'upvoted') {
        setUserVote('upvote');
        setVotes(prev => prev + 1);
      } else if (response.action === 'removed') {
        setUserVote(null);
        setVotes(prev => prev - 1);
      } else if (response.action === 'changed') {
        setUserVote('upvote');
        setVotes(prev => prev + 2); // +2 because we're changing from downvote to upvote
      }
      
      // Update vote count from API response if available
      if (response.votes !== undefined) {
        setVotes(response.votes);
      }
      
      // Notify parent component with the new vote count
      const newVoteCount = response.votes !== undefined ? response.votes : 
                          votes + (response.action === 'upvoted' ? 1 : response.action === 'removed' ? -1 : response.action === 'changed' ? 2 : 0);
      if (onVoteChange) {
        onVoteChange(itemId, response.action, newVoteCount);
      }
      
      toast.success(response.message);
    } catch (error) {
      console.error('Error upvoting:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.status === 404) {
        toast.error('Voting endpoints not yet implemented on server');
      } else {
        toast.error('Failed to upvote item');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownvote = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      const response = await clipboardService.downvoteItem(itemId);
      console.log('Downvote response:', response);
      
      // Update local state based on API response
      if (response.action === 'downvoted') {
        setUserVote('downvote');
        setVotes(prev => prev - 1);
      } else if (response.action === 'removed') {
        setUserVote(null);
        setVotes(prev => prev + 1);
      } else if (response.action === 'changed') {
        setUserVote('downvote');
        setVotes(prev => prev - 2); // -2 because we're changing from upvote to downvote
      }
      
      // Update vote count from API response if available
      if (response.votes !== undefined) {
        setVotes(response.votes);
      }
      
      // Notify parent component
      if (onVoteChange) {
        const newVoteCount = response.votes !== undefined ? response.votes : votes;
        onVoteChange(itemId, response.action, newVoteCount);
      }
      
      toast.success(response.message);
    } catch (error) {
      console.error('Error downvoting:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.status === 404) {
        toast.error('Voting endpoints not yet implemented on server');
      } else {
        toast.error('Failed to downvote item');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center space-x-2">
      {/* Upvote button */}
      <button
        type="button"
        onClick={handleUpvote}
        disabled={loading || !endpointsAvailable}
        className={`p-1.5 rounded transition-colors duration-200 ${
          userVote === 'upvote'
            ? 'text-green-600 bg-green-50 hover:bg-green-100'
            : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
        } ${loading || !endpointsAvailable ? 'opacity-50 cursor-not-allowed' : ''}`}
        title={!endpointsAvailable ? 'Voting not yet available' : 'Upvote'}
      >
        <ThumbsUp className={`h-4 w-4 ${loading ? 'animate-pulse' : ''}`} />
      </button>
      
      {/* Vote count */}
      <span className={`text-sm font-medium min-w-[2rem] text-center ${
        votes > 0 ? 'text-green-600' : votes < 0 ? 'text-red-600' : 'text-gray-700'
      }`}>
        {votes}
      </span>
      
      {/* Downvote button */}
      <button
        type="button"
        onClick={handleDownvote}
        disabled={loading || !endpointsAvailable}
        className={`p-1.5 rounded transition-colors duration-200 ${
          userVote === 'downvote'
            ? 'text-red-600 bg-red-50 hover:bg-red-100'
            : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
        } ${loading || !endpointsAvailable ? 'opacity-50 cursor-not-allowed' : ''}`}
        title={!endpointsAvailable ? 'Voting not yet available' : 'Downvote'}
      >
        <ThumbsDown className={`h-4 w-4 ${loading ? 'animate-pulse' : ''}`} />
      </button>
    </div>
  );
};

export default HorizontalVoteButtons; 